import { Link, useParams } from "react-router-dom";
import { projects } from "@/content/site";
import { CtaBlock, TextLink, BrowserFrame, PhoneFrame, Label, Btn } from "@/components/site/Ui";
import { Clip, MaskLines, Reveal } from "@/components/site/Motion";
import { Deliverables } from "@/components/site/Projects";
import BrandGrid from "@/components/site/BrandGrid";
import { useSeo } from "@/lib/seo";
import NotFound from "./NotFound";

export default function ProjectDetail() {
  const { slug } = useParams();
  const p = projects.find((x) => x.slug === slug);
  useSeo({
    title: p ? `${p.name} — étude de cas` : "Projet introuvable",
    description: p ? `${p.place}. ${p.context.split(". ")[0]}.` : "Ce projet n'existe pas.",
    path: `/realisations/${slug}`, noindex: !p,
  });
  if (!p) return <NotFound />;
  const next = projects[(projects.indexOf(p) + 1) % projects.length];
  return (
    <>
      <section className="wrap pt-32 pb-10 md:pt-44" data-testid="project-detail">
        <Reveal className="mb-8 flex flex-wrap items-center gap-4">
          <Link to="/realisations" className="link-u text-[14px] font-semibold" data-testid="project-back">← Réalisations</Link>
          <span className="label">({String(projects.indexOf(p) + 1).padStart(2, "0")}) {p.category}</span>
        </Reveal>
        <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-8">
            <Label className="mb-5">{p.place}</Label>
            <MaskLines as="h1" onLoad lines={[p.name]} className="display h-xl" />
          </div>
          {p.url && <Reveal delay={0.2} className="lg:col-span-4 lg:text-right"><Btn href={p.url} target="_blank" rel="noopener noreferrer" testid="project-visit">Voir le site</Btn></Reveal>}
        </div>
      </section>
      <section className="wrap pb-14"><Clip><BrowserFrame src={p.shots.home} alt={`Page d'accueil du site ${p.name}`} label={p.domain || p.name} priority /></Clip></section>
      <section className="wrap grid gap-10 border-t border-ink/20 py-14 md:py-20 lg:grid-cols-12">
        <Reveal className="lg:col-span-5"><p className="label mb-4">Contexte</p><p className="text-[19px] leading-relaxed">{p.context}</p></Reveal>
        <Reveal delay={0.08} className="space-y-10 lg:col-span-6 lg:col-start-7">
          <div><p className="label mb-4">Livrables</p><Deliverables items={p.deliverables} /></div>
          <div>
            <p className="label mb-4">Palette</p>
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="project-palette">
              {p.palette.map((c) => (
                <li key={c.hex}><span className="block h-16 border border-ink/15" style={{ background: c.hex }} /><span className="mt-2 block text-[14px] font-medium">{c.name}</span><span className="font-mono text-[12px] text-mute">{c.hex}</span></li>
              ))}
            </ul>
          </div>
        </Reveal>
      </section>
      {p.slug === "maison-sanaa" && (
        <section id="univers-de-marque" className="wrap border-t border-ink/20 py-14 md:py-20" data-testid="brand-universe">
          <Reveal className="mb-10 grid gap-6 lg:grid-cols-12">
            <h2 className="display h-md lg:col-span-5 md:text-[2.6rem]">Univers de marque — concept produit</h2>
            <p className="text-[19px] leading-relaxed lg:col-span-6 lg:col-start-7">Prolonger l'identité du site jusqu'aux produits : même typographie, mêmes repères de coupe, même noir profond.</p>
          </Reveal>
          <BrandGrid testid="case-brand-grid" />
        </section>
      )}
      <section className="wrap grid gap-8 pb-20 lg:grid-cols-12">
        <div className="space-y-8 lg:col-span-9">
          <Clip><BrowserFrame src={p.shots.s1} alt={`Section du site ${p.name}`} label={p.domain || p.name} /></Clip>
          <Clip><BrowserFrame src={p.shots.s2} alt={`Autre section du site ${p.name}`} label={p.domain || p.name} /></Clip>
        </div>
        <div className="lg:col-span-3"><div className="mx-auto max-w-[260px] lg:sticky lg:top-28"><PhoneFrame src={p.shots.mobile} alt={`Version mobile du site ${p.name}`} /><p className="mt-3 text-center font-mono text-[12px] text-mute">Version mobile</p></div></div>
      </section>
      <section className="wrap border-t border-ink/20 py-10"><TextLink to={`/realisations/${next.slug}`} testid="project-next">Projet suivant : {next.name}</TextLink></section>
      <CtaBlock />
    </>
  );
}
